import type { ReactNode } from 'react';

import { Badge, DEFAULT_MAX_VALUE, type IBadgeProps } from './Badge'
import {
  WithBadgeStyled,
  type IWithBadgeStyled,
} from './styles/WithBadgeStyled'

export interface IWithBadgeProps extends IWithBadgeStyled {
  /**
   * Optional id for the wrapper element.
   */
  id?: string
  /**
   * Whether to display the badge.
   */
  show?: boolean
  /**
   * Element to anchor the badge to (usually an icon or button).
   */
  children?: ReactNode
  /**
   * Accessible label for the badge.
   */
  ariaLabel?: IBadgeProps['ariaLabel']
  /**
   * Color of the badge. Allowed values: 'primary' | 'danger'.
   */
  color?: IBadgeProps['color']
  /**
   * Content displayed inside the badge (number, string, or element).
   */
  content?: IBadgeProps['children']
  /**
   * Maximum value before showing 'max+' (e.g., 99+).
   */
  maxValue?: IBadgeProps['maxValue']
}

/**
 * Displays a badge anchored to a child element.
 *
 * Renders a badge (such as a notification count) positioned relative to its child element.
 * Supports custom anchor positions (`placement`), vertical and horizontal offsets, badge color, maximum value display, and accessibility options.
 * The badge can be shown or hidden, and its content can be a number, string, or element.
 */
export const WithBadge = ({
  id,
  show = true,
  children,
  ariaLabel,
  color = 'primary',
  content,
  maxValue = DEFAULT_MAX_VALUE,
  placement = 'top-right',
  verticalOffset = 2,
  horizontalOffset = 2,
}: IWithBadgeProps) => (
  <WithBadgeStyled
    id={id}
    placement={placement}
    verticalOffset={verticalOffset}
    horizontalOffset={horizontalOffset}
  >
    {children}
    {show && (
      <Badge color={color} maxValue={maxValue} ariaLabel={ariaLabel}>
        {content}
      </Badge>
    )}
  </WithBadgeStyled>
)
